import type { CompanyBillingPage as CompanyBillingPageTypes } from "../CompanyBillingPage/types";
import { formatCompanyBillingDate, formatCompanyBillingPlanLabel } from "./formatting";
import { getCompanyBillingCurrentPlanDefinition, findCompanyBillingPlanDefinition } from "./plans";
import { formatStorageBytes } from "./storageFormatting";
import i18n from "../i18n";

export function resolveCompanyBillingChangeTiming(
  currentTarget: CompanyBillingPageTypes.BillingTarget | null,
  nextTarget: CompanyBillingPageTypes.BillingTarget | null,
  plans: CompanyBillingPageTypes.BillingPlanDefinition[],
): CompanyBillingPageTypes.ChangeTiming | null {
  if (!currentTarget || !nextTarget) return null;

  const currentTier = planTier(plans, currentTarget.plan);
  const nextTier = planTier(plans, nextTarget.plan);

  if (nextTier > currentTier) return "immediate";
  if (nextTier < currentTier) return "next_renewal";

  if (currentTarget.billingInterval === "monthly" && nextTarget.billingInterval === "yearly") {
    return "immediate";
  }

  if (currentTarget.billingInterval === "yearly" && nextTarget.billingInterval === "monthly") {
    return "next_renewal";
  }

  return "immediate";
}

export function buildCompanyBillingChangeConsequence(args: {
  billing: CompanyBillingPageTypes.BillingOverview;
  targetPlanKey: CompanyBillingPageTypes.ChangeTargetPlan;
  targetBillingInterval?: CompanyBillingPageTypes.Interval | null;
  timing: CompanyBillingPageTypes.ChangeTiming;
  effectiveDate?: string | null;
}): CompanyBillingPageTypes.ChangeConsequence {
  const { billing, targetPlanKey, targetBillingInterval, timing, effectiveDate = null } = args;
  const currentPlan = getCompanyBillingCurrentPlanDefinition(billing);
  const targetPlan = findCompanyBillingPlanDefinition(billing.plans, targetPlanKey);
  const memberLimit = targetPlan?.memberLimit ?? null;
  const storageLimitBytes = targetPlan?.storageLimitBytes ?? null;
  const memberOverage = memberLimit == null ? 0 : Math.max(billing.memberCount - memberLimit, 0);
  const storageOverageBytes =
    storageLimitBytes == null ? 0 : Math.max(billing.storageUsageBytes - storageLimitBytes, 0);

  return {
    targetPlanKey,
    targetPlanLabel: formatCompanyBillingPlanLabel(targetPlanKey, targetBillingInterval),
    timing,
    effectiveDate,
    isLowerEntitlement: !!currentPlan && !!targetPlan && isLowerEntitlement(currentPlan, targetPlan),
    memberCount: billing.memberCount,
    memberLimit,
    memberOverage,
    storageUsageBytes: billing.storageUsageBytes,
    storageLimitBytes,
    storageOverageBytes,
    overageKind: determineOverageKind(memberOverage, storageOverageBytes),
  };
}

export function formatCompanyBillingChangeTimingDescription(
  consequence: CompanyBillingPageTypes.ChangeConsequence,
): string {
  if (consequence.timing === "immediate") {
    return i18n.t("{{plan}} takes effect immediately.", { plan: consequence.targetPlanLabel });
  }

  const effectiveDate = formatCompanyBillingDate(consequence.effectiveDate);
  if (effectiveDate) {
    return i18n.t("{{plan}} takes effect at the next renewal on {{date}}.", {
      plan: consequence.targetPlanLabel,
      date: effectiveDate,
    });
  }

  return i18n.t("{{plan}} takes effect at the next renewal.", { plan: consequence.targetPlanLabel });
}

export function buildCompanyBillingOverageDescription(consequence: CompanyBillingPageTypes.ChangeConsequence) {
  const limitLabel = consequence.targetPlanLabel;
  const memberLimit = consequence.memberLimit;
  const storageLimitBytes = consequence.storageLimitBytes;

  let description: string | null = null;

  switch (consequence.overageKind) {
    case "member":
      description =
        memberLimit == null
          ? null
          : i18n.t(
              "After it takes effect, adding or restoring people may be blocked because this company has {{memberCount}} active members and {{plan}} includes {{memberLimit}}.",
              {
                memberCount: consequence.memberCount,
                plan: limitLabel,
                memberLimit,
              },
            );
      break;

    case "storage":
      description =
        storageLimitBytes == null
          ? null
          : i18n.t(
              "After it takes effect, uploading files may be blocked because this company is using {{storage}} and {{plan}} includes {{limit}}.",
              {
                storage: formatStorageBytes(consequence.storageUsageBytes),
                plan: limitLabel,
                limit: formatStorageBytes(storageLimitBytes),
              },
            );
      break;

    case "member_and_storage":
      description =
        memberLimit == null || storageLimitBytes == null
          ? null
          : i18n.t(
              "After it takes effect, adding or restoring people and uploading files may be blocked because this company has {{memberCount}} active members, is using {{storage}}, and {{plan}} includes {{memberLimit}} members and {{storageLimit}} of storage.",
              {
                memberCount: consequence.memberCount,
                storage: formatStorageBytes(consequence.storageUsageBytes),
                plan: limitLabel,
                memberLimit,
                storageLimit: formatStorageBytes(storageLimitBytes),
              },
            );
      break;

    default:
      description = null;
  }

  if (!description) return null;

  return description;
}

function determineOverageKind(memberOverage: number, storageOverageBytes: number): CompanyBillingPageTypes.OverageKind {
  if (memberOverage > 0 && storageOverageBytes > 0) return "member_and_storage";
  if (memberOverage > 0) return "member";
  if (storageOverageBytes > 0) return "storage";
  return "none";
}

function planTier(
  plans: CompanyBillingPageTypes.BillingPlanDefinition[],
  planKey: CompanyBillingPageTypes.PlanKey,
): number {
  return findCompanyBillingPlanDefinition(plans, planKey)?.tierRank ?? -1;
}

function isLowerEntitlement(
  currentPlan: CompanyBillingPageTypes.BillingPlanDefinition,
  targetPlan: CompanyBillingPageTypes.BillingPlanDefinition,
): boolean {
  const memberLimitDecreases =
    currentPlan.memberLimit != null &&
    targetPlan.memberLimit != null &&
    targetPlan.memberLimit < currentPlan.memberLimit;

  const storageLimitDecreases =
    currentPlan.storageLimitBytes != null &&
    targetPlan.storageLimitBytes != null &&
    targetPlan.storageLimitBytes < currentPlan.storageLimitBytes;

  return memberLimitDecreases || storageLimitDecreases;
}
